import React, { useState, useEffect } from "react";
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Grid,
  FormHelperText,
} from "@mui/material";

const Choice = ({
  question,
  onChange,
  hideLabel = false,
  placeholder = "",
  multiHint = "",
}) => {
  // Initialize the value with the existing answer
  const initialValue =
    question.questionType === "multipleChoice" ||
    (question.questionType === "related" && question.matchingCriteria === "multiple")
      ? question.answer
        ? question.answer.split(",")
        : []
      : question.answer || "";

  const [value, setValue] = useState(initialValue);

  // Re-sync when the parent rewrites the answer (e.g. a failed save rolled back).
  useEffect(() => {
    setValue(
      question.questionType === "multipleChoice" ||
        (question.questionType === "related" &&
          question.matchingCriteria === "multiple")
        ? question.answer
          ? question.answer.split(",")
          : []
        : question.answer || ""
    );
  }, [question.answer, question.questionType, question.matchingCriteria]);
  
  // State to control the open state of the Select dropdown
  const [open, setOpen] = useState(false);

  const handleChange = (event) => {
    const newValue = event.target.value;

    setValue(newValue); // Update local state

    // Format the value for multiple or single selection
    const formattedValue =
      question.questionType === "multipleChoice" ||
      (question.questionType === "related" && question.matchingCriteria === "multiple")
        ? Array.isArray(newValue)
          ? newValue.join(",")
          : newValue
        : newValue;
    onChange(question._id, formattedValue); // Notify parent
  };

  // Handlers to open and close the dropdown
  const handleOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  // Determine if the select should allow multiple selections
  const isMultiple =
    question.questionType === "multipleChoice" ||
    (question.questionType === "related" && question.matchingCriteria === "multiple");

  // Determine if there's an error: required and no answer
  const isError =
    !hideLabel &&
    question.required &&
    (value === "" ||
      value === null ||
      (Array.isArray(value) && value.length === 0));

  return (
    <Grid item xs={12}>
      <FormControl fullWidth error={isError}>
        {!hideLabel && (
          <InputLabel>
            {question.question}
            {question.required ? " *" : ""}
          </InputLabel>
        )}
        <Select
          multiple={isMultiple} // Correctly passing a boolean
          label={hideLabel ? undefined : question.question}
          displayEmpty={hideLabel}
          value={value}
          onChange={handleChange}
          open={open} // Controlled open state
          onClose={handleClose} // Handle close event
          onOpen={handleOpen} // Handle open event
          renderValue={(selected) => {
            const isEmpty = Array.isArray(selected)
              ? selected.length === 0
              : selected === "" || selected === null || selected === undefined;
            if (isEmpty) {
              return hideLabel ? (
                <span style={{ opacity: 0.55 }}>{placeholder}</span>
              ) : (
                ""
              );
            }
            return Array.isArray(selected)
              ? selected
                  .map(
                    (val) =>
                      question.choices.find((c) => c.value === val)?.label || ""
                  )
                  .join(", ")
              : question.choices.find((c) => c.value === selected)?.label || "";
          }}
        >
          {question.choices.map((choice) => (
            <MenuItem key={choice.value} value={choice.value}>
              {choice.label}
            </MenuItem>
          ))}
        </Select>
        {/* Conditionally render FormHelperText for errors */}
        {isError && (
          <FormHelperText>This field is required.</FormHelperText>
        )}
        {hideLabel && isMultiple && multiHint && (
          <FormHelperText>{multiHint}</FormHelperText>
        )}
        {!hideLabel && question.description && (
          <Typography variant="caption" color="textSecondary" mt={1}>
            {question.description}
          </Typography>
        )}
      </FormControl>
    </Grid>
  );
};

export default Choice;